// src/pages/ContactsPage.js — Global contacts list (all contacts ever)
// [marker: data-grid v1] Excel-style grid: every column sortable/filterable,
// show/hide + resize columns (saved per staff member), select rows → bulk tags.
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { TagBadge } from './ContactTagsSettingsPage';
import DataGrid from '../components/DataGrid';
import { DuplicatesModal } from '../components/ContactMerge'; // [marker: contact-merge v1] [marker: invoice-links v1]

const human = (s) => (s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, m => m.toUpperCase()) : '');

const CONTACT_COLUMNS = [
  { key: 'name', label: 'Name', width: 190, get: r => `${r.last_name || ''} ${r.first_name || ''}`.trim(),
    text: r => `${r.first_name || ''} ${r.last_name || ''}`.trim(),
    render: r => <strong>{r.first_name} {r.last_name}</strong> },
  { key: 'first_name', label: 'First Name', width: 120, defaultHidden: true },
  { key: 'last_name', label: 'Last Name', width: 120, defaultHidden: true },
  { key: 'salutation', label: 'Salutation', width: 90, defaultHidden: true },
  { key: 'title', label: 'Title', width: 150, defaultHidden: true },
  { key: 'email', label: 'Email', width: 220 },
  { key: 'email_error', label: 'Email Error', width: 90, text: r => (r.email_error ? 'X' : ''),
    render: r => r.email_error ? <span className="badge badge-danger" style={{ fontSize: '0.65rem' }}>X</span> : null },
  { key: 'phone', label: 'Phone', width: 130 },
  { key: 'secondary_email', label: 'Secondary Email', width: 200, defaultHidden: true },
  { key: 'secondary_phone', label: 'Secondary Phone', width: 130, defaultHidden: true },
  { key: 'organization', label: 'Business / Affiliation', width: 220,
    get: r => r.business_name || r.company_name || r.office_or_org || '' },
  { key: 'company_name', label: 'Company (typed)', width: 180, defaultHidden: true },
  { key: 'kind', label: 'Kind', width: 120, text: r => (r.business_id ? 'Member contact' : 'Standalone') },
  { key: 'business_status', label: 'Business Status', width: 120, defaultHidden: true, text: r => human(r.business_status) },
  { key: 'membership_type_name', label: 'Membership', width: 130, defaultHidden: true },
  { key: 'tags', label: 'Tags', width: 220, type: 'list', list: r => (r.tags || []).map(t => t.name),
    render: r => (
      <span style={{ display: 'inline-flex', gap: 4 }}>
        {(r.tags || []).map(t => <TagBadge key={t.id} tag={t} size="sm" />)}
      </span>
    ) },
  { key: 'vip', label: 'VIP', width: 90 },
  { key: 'association', label: 'Association', width: 100 },
  { key: 'contact_type', label: 'Type', width: 130, text: r => human(r.contact_type) },
  { key: 'portal_role', label: 'Portal Role', width: 120, defaultHidden: true, text: r => human(r.portal_role) },
  { key: 'email_blast_opt_in', label: 'Blast Opt-In', width: 95, type: 'bool', defaultHidden: true },
  { key: 'email_unsubscribed', label: 'Unsubscribed', width: 100, type: 'bool', defaultHidden: true },
  { key: 'government_level', label: 'Gov. Level', width: 100, defaultHidden: true, text: r => human(r.government_level) },
  { key: 'address_line1', label: 'Address', width: 200, defaultHidden: true },
  { key: 'city', label: 'City', width: 110, defaultHidden: true },
  { key: 'state', label: 'State', width: 70, defaultHidden: true },
  { key: 'zip_code', label: 'Zip', width: 80, defaultHidden: true },
  { key: 'birthday', label: 'Birthday', width: 100, type: 'date', defaultHidden: true },
  { key: 'tickets_count', label: 'Tickets', width: 80, type: 'number', defaultHidden: true },
  { key: 'last_event_date', label: 'Last Event', width: 105, type: 'date', defaultHidden: true },
  { key: 'last_contacted_at', label: 'Last Contacted', width: 115, type: 'date', defaultHidden: true },
  { key: 'portal_last_login_at', label: 'Last Portal Login', width: 125, type: 'date', defaultHidden: true },
  { key: 'created_at', label: 'Added', width: 100, type: 'date', defaultHidden: true },
];

export default function ContactsPage() {
  const navigate = useNavigate();
  const { isManagerOrAbove } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkTags, setShowBulkTags] = useState(false);
  const [dupCount, setDupCount] = useState(0); // [marker: contact-merge v1]
  const [showDups, setShowDups] = useState(false);
  const loadDups = () => api.get('/contact-merge/duplicates').then(r => setDupCount((r.data || []).length)).catch(() => {});
  useEffect(() => { if (isManagerOrAbove()) loadDups(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContacts = () => {
    setLoading(true);
    api.get('/contacts/grid')
      .then(res => setRows(res.data || []))
      .catch(() => toast.error('Failed to load contacts.'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { loadContacts(); }, []);

  const handleContactSaved = () => {
    setShowModal(false);
    loadContacts();
    toast.success('Contact saved.');
  };

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 12 }}>
        <h1>Contacts</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {isManagerOrAbove() && (
            <>
              <button className="btn btn-secondary" onClick={() => setShowDups(true)}>Find Duplicates</button>
              <Link to="/contact-tags" className="btn btn-secondary">Manage Tags</Link>
              <button className="btn btn-primary" onClick={() => setShowModal(true)}>+ New Contact</button>
            </>
          )}
        </div>
      </div>

      {isManagerOrAbove() && dupCount > 0 && (
        <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '8px 12px', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <span>⚠️ {dupCount} possible duplicate contact{dupCount === 1 ? '' : 's'} found (same email, same name, or same phone and last name).</span>
          <button className="btn btn-primary btn-sm" onClick={() => setShowDups(true)}>Review</button>
        </div>
      )}

      <DataGrid
        gridKey="contacts"
        columns={CONTACT_COLUMNS}
        rows={rows}
        loading={loading}
        onRowClick={r => navigate(`/contacts/${r.id}`)}
        selectable={isManagerOrAbove()}
        selectedIds={selected}
        onSelectionChange={setSelected}
        toolbarLeft={isManagerOrAbove() && selected.length > 0 && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowBulkTags(true)}>
            🏷 Tags for {selected.length} selected…
          </button>
        )}
      />

      {showModal && (
        <ContactFormModal
          contact={null}
          defaultAttached={true}   /* per user preference: most new entries are member staff */
          onClose={() => setShowModal(false)}
          onSaved={handleContactSaved}
        />
      )}

      {showDups && (
        <DuplicatesModal onClose={() => { setShowDups(false); loadDups(); }} onChanged={() => { loadContacts(); loadDups(); }} />
      )}

      {showBulkTags && (
        <BulkTagsModal
          contactIds={selected}
          onClose={() => setShowBulkTags(false)}
          onDone={() => { setShowBulkTags(false); loadContacts(); }}
        />
      )}
    </div>
  );
}

// ─── BulkTagsModal — add / remove tags on many contacts ── [marker: data-grid v1]
function BulkTagsModal({ contactIds, onClose, onDone }) {
  const [tags, setTags] = useState([]);
  const [mode, setMode] = useState('add');
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/contact-tags')
      .then(r => setTags((r.data || []).filter(t => t.is_active !== false)))
      .catch(() => toast.error('Could not load tags.'));
  }, []);

  const toggle = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const save = async () => {
    if (!picked.length) return toast.error('Pick at least one tag.');
    setSaving(true);
    try {
      const body = { contactIds: contactIds.map(Number) };
      if (mode === 'add') body.addTagIds = picked; else body.removeTagIds = picked;
      const r = await api.post('/contacts/bulk-tags', body);
      toast.success(mode === 'add'
        ? `Added ${r.data.added} tag${r.data.added === 1 ? '' : 's'} across ${r.data.contacts} contacts.`
        : `Removed ${r.data.removed} tag${r.data.removed === 1 ? '' : 's'} from ${r.data.contacts} contacts.`);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update tags.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
        <div className="modal-header">
          <h2>Tags for {contactIds.length} contact{contactIds.length === 1 ? '' : 's'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', gap: 16 }}>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={mode === 'add'} onChange={() => setMode('add')} /> Add tags
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>
              <input type="radio" checked={mode === 'remove'} onChange={() => setMode('remove')} /> Remove tags
            </label>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 280, overflowY: 'auto' }}>
            {tags.length === 0 && <span style={{ color: 'var(--gray-500)' }}>No tags yet — create them under Manage Tags.</span>}
            {tags.map(t => (
              <label key={t.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                padding: '4px 8px', borderRadius: 8, border: '1px solid var(--gray-200)',
                background: picked.includes(t.id) ? 'var(--navy-ghost)' : 'white' }}>
                <input type="checkbox" checked={picked.includes(t.id)} onChange={() => toggle(t.id)} />
                <TagBadge tag={t} size="sm" />
              </label>
            ))}
          </div>
        </div>
        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className={`btn ${mode === 'add' ? 'btn-primary' : 'btn-danger'}`} onClick={save} disabled={saving || !picked.length}>
            {saving ? 'Saving…' : mode === 'add' ? 'Add to selected' : 'Remove from selected'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── ContactFormModal ───────────────────────────────────────────────────────
// Used for both create and edit. Pass `contact` for edit, null for create.
// Exported so ContactDetailPage can reuse it.
export function ContactFormModal({ contact, defaultAttached = true, onClose, onSaved }) {
  const isEdit = !!contact;

  const [form, setForm] = useState({
    firstName:       contact?.first_name        ?? '',
    lastName:        contact?.last_name         ?? '',
    title:           contact?.title             ?? '',
    email:           contact?.email             ?? '',
    secondaryEmail:  contact?.secondary_email   ?? '',
    phone:           contact?.phone             ?? '',
    secondaryPhone:  contact?.secondary_phone   ?? '',
    companyName:     contact?.company_name      ?? '',
    vip:             contact?.vip               ?? '',
    association:     contact?.association       ?? '',
    birthday:        contact?.birthday          ? contact.birthday.slice(0, 10) : '',
    addressLine1:    contact?.address_line1     ?? '',
    addressLine2:    contact?.address_line2     ?? '',
    city:            contact?.city              ?? '',
    state:           contact?.state             ?? '',
    zipCode:         contact?.zip_code          ?? '',
    contactType:     contact?.contact_type      ?? 'general_staff',
    portalRole:      contact?.portal_role       ?? 'member_contact',
    contactFrequency:contact?.contact_frequency ?? 'as_needed',
    officeOrOrg:     contact?.office_or_org     ?? '',
    governmentLevel: contact?.government_level  ?? '',
    businessId:      contact?.business_id       ?? '',
    emailError:      contact?.email_error ? 'X' : '', /* [marker: email-bounce-detect v1] */
  });
  const [attached, setAttached] = useState(
    isEdit ? !!contact.business_id : defaultAttached
  );
  const [businesses, setBusinesses] = useState([]);
  const [businessSearch, setBusinessSearch] = useState('');
  const [businessesLoading, setBusinessesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const businessesLoaded = useRef(false);

  // Load businesses when we need them (attached mode)
  useEffect(() => {
    if (!attached || businessesLoaded.current) return;
    setBusinessesLoading(true);
    api.get('/businesses?limit=500')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data.data || res.data.businesses || []);
        setBusinesses(list);
        businessesLoaded.current = true;
      })
      .catch(() => toast.error('Could not load businesses.'))
      .finally(() => setBusinessesLoading(false));
  }, [attached]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // [marker: email-bounce-detect v1] changing the primary email clears the Email Error X
  const handleEmailChange = (v) => {
    setForm(f => {
      const next = { ...f, email: v };
      if (isEdit && contact?.email_error &&
          String(v || '').trim().toLowerCase() !== String(contact.email || '').trim().toLowerCase()) {
        next.emailError = '';
      }
      return next;
    });
  };

  const filteredBusinesses = businessSearch
    ? businesses.filter(b => b.business_name?.toLowerCase().includes(businessSearch.toLowerCase()))
    : businesses;

  const handleSave = async () => {
    if (!form.firstName.trim() || !form.lastName.trim()) {
      toast.error('First and last name are required.');
      return;
    }
    if (attached && !form.businessId) {
      toast.error('Choose a business or switch to Standalone.');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        firstName:        form.firstName.trim(),
        lastName:         form.lastName.trim(),
        title:            form.title || null,
        email:            form.email || null,
        secondaryEmail:   form.secondaryEmail || null,
        phone:            form.phone || null,
        secondaryPhone:   form.secondaryPhone || null,
        companyName:      form.companyName || null,
        vip:              form.vip || null,
        association:      form.association || null,
        birthday:         form.birthday || null,
        addressLine1:     form.addressLine1 || null,
        addressLine2:     form.addressLine2 || null,
        city:             form.city || null,
        state:            form.state || null,
        zipCode:          form.zipCode || null,
        contactType:      form.contactType,
        portalRole:       form.portalRole,
        contactFrequency: form.contactFrequency,
        officeOrOrg:      form.officeOrOrg || null,
        governmentLevel:  form.governmentLevel || null,
        businessId:       attached ? parseInt(form.businessId, 10) : null,
      };
      if (isEdit) payload.emailError = form.emailError === 'X'; // [marker: email-bounce-detect v1]

      if (isEdit) {
        await api.patch(`/contacts/${contact.id}`, payload);
      } else {
        await api.post('/contacts', payload);
      }
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Contact' : 'New Contact'}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Attached / Standalone toggle */}
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', padding: 12, background: 'var(--gray-50)', borderRadius: 6 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={attached}  onChange={() => setAttached(true)} />
              Attached to a business
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={!attached} onChange={() => setAttached(false)} />
              Standalone (politician, vendor, etc.)
            </label>
          </div>

          {/* Business picker (if attached) */}
          {attached && (
            <div>
              <label className="form-label">Business *</label>
              <input
                type="text"
                className="form-input"
                placeholder="Search businesses…"
                value={businessSearch}
                onChange={e => setBusinessSearch(e.target.value)}
                style={{ marginBottom: 6 }}
              />
              {/* [marker: business-picker-clickable v1]
                  A native sized <select> doesn't reliably fire onChange on click
                  (React controlled-value quirk), so clicking a business often
                  left businessId unset. This is an explicit clickable list: each
                  row sets businessId directly on click. */}
              <div
                className="form-input"
                style={{ width: '100%', maxHeight: 180, overflowY: 'auto', padding: 0 }}
              >
                {businessesLoading && (
                  <div style={{ padding: '8px 12px', color: 'var(--gray-500)' }}>Loading…</div>
                )}
                {!businessesLoading && filteredBusinesses.length === 0 && (
                  <div style={{ padding: '8px 12px', color: 'var(--gray-500)' }}>No businesses match</div>
                )}
                {filteredBusinesses.map(b => {
                  const selected = String(form.businessId) === String(b.id);
                  return (
                    <div
                      key={b.id}
                      onClick={() => set('businessId', String(b.id))}
                      style={{
                        padding: '8px 12px',
                        cursor: 'pointer',
                        background: selected ? 'var(--primary, #1a3a5c)' : 'transparent',
                        color: selected ? '#fff' : 'var(--gray-900)',
                        fontWeight: selected ? 600 : 400,
                        borderBottom: '1px solid var(--gray-100)',
                      }}
                      onMouseEnter={e => { if (!selected) e.currentTarget.style.background = 'var(--gray-100)'; }}
                      onMouseLeave={e => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                    >
                      {b.business_name}
                    </div>
                  );
                })}
              </div>
              {form.businessId && (
                <div style={{ fontSize: '0.8rem', color: 'var(--gray-600)', marginTop: 4 }}>
                  Selected: <strong>{businesses.find(b => String(b.id) === String(form.businessId))?.business_name || '—'}</strong>
                </div>
              )}
            </div>
          )}

          {/* Company (free-text write-in) — [marker: contact-extra-fields v1] */}
          <div>
            <label className="form-label">Company</label>
            <input
              className="form-input"
              value={form.companyName}
              onChange={e => set('companyName', e.target.value)}
              placeholder="Type a company name (if it's not a listed business)"
            />
          </div>

          {/* Name */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">First Name *</label>
              <input className="form-input" value={form.firstName} onChange={e => set('firstName', e.target.value)} />
            </div>
            <div>
              <label className="form-label">Last Name *</label>
              <input className="form-input" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
          </div>

          {/* Title / Email / Phone */}
          <div>
            <label className="form-label">Title</label>
            <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. CEO, Senator, Director" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Email</label>
              <input className="form-input" type="email" value={form.email} onChange={e => handleEmailChange(e.target.value)} />
            </div>
            <div>
              <label className="form-label">Phone</label>
              <input className="form-input" value={form.phone} onChange={e => set('phone', e.target.value)} />
            </div>
          </div>

          {/* Secondary email / Secondary phone */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Secondary Email</label>
              <input className="form-input" type="email" value={form.secondaryEmail} onChange={e => set('secondaryEmail', e.target.value)} placeholder="Used if the primary email is blank" />
            </div>
            <div>
              <label className="form-label">Secondary Phone</label>
              <input className="form-input" value={form.secondaryPhone} onChange={e => set('secondaryPhone', e.target.value)} placeholder="e.g. cell, if there's a second number" />
            </div>
          </div>

          {/* VIP / Association */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">VIP</label>
              <input className="form-input" value={form.vip} onChange={e => set('vip', e.target.value)} placeholder="e.g. VIP, League" />
            </div>
            <div>
              <label className="form-label">Association</label>
              <input className="form-input" value={form.association} onChange={e => set('association', e.target.value)} placeholder="e.g. RE, g, M" />
            </div>
          </div>

          {/* Email Error — [marker: email-bounce-detect v1] edit only */}
          {isEdit && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'start' }}>
              <div>
                <label className="form-label">Email Error</label>
                <select className="form-select" value={form.emailError} onChange={e => set('emailError', e.target.value)}>
                  <option value="">(blank)</option>
                  <option value="X">X</option>
                </select>
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', paddingTop: 24 }}>
                {form.emailError === 'X'
                  ? (contact?.email_error
                      ? <>Bounced{contact.email_error_address ? <> ({contact.email_error_address})</> : null}{contact.email_error_at ? <> on {new Date(contact.email_error_at).toLocaleDateString()}</> : null}. Excluded from email blasts.</>
                      : <>Will be excluded from email blasts.</>)
                  : (contact?.email_error
                      ? <>Will be cleared on save. This contact will receive blasts again.</>
                      : <>Set automatically when a blast to this address bounces. Clears when the email is changed.</>)}
              </div>
            </div>
          )}

          {/* Birthday */}
          <div>
            <label className="form-label">Birthday</label>
            <input className="form-input" type="date" value={form.birthday} onChange={e => set('birthday', e.target.value)} style={{ maxWidth: 220 }} />
          </div>

          {/* Address */}
          <fieldset style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12 }}>
            <legend style={{ padding: '0 6px', fontSize: '0.85rem', color: 'var(--gray-500)' }}>Personal Address (optional)</legend>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <input className="form-input" placeholder="Address line 1" value={form.addressLine1} onChange={e => set('addressLine1', e.target.value)} />
              <input className="form-input" placeholder="Address line 2" value={form.addressLine2} onChange={e => set('addressLine2', e.target.value)} />
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
                <input className="form-input" placeholder="City"  value={form.city}    onChange={e => set('city', e.target.value)} />
                <input className="form-input" placeholder="State" value={form.state}   onChange={e => set('state', e.target.value)} />
                <input className="form-input" placeholder="Zip"   value={form.zipCode} onChange={e => set('zipCode', e.target.value)} />
              </div>
            </div>
          </fieldset>

          {/* Type / Frequency / Portal Role */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label className="form-label">Contact Type</label>
              <select className="form-select" value={form.contactType} onChange={e => set('contactType', e.target.value)}>
                <option value="primary_contact">Primary Contact</option>
                <option value="decision_maker">Decision Maker</option>
                <option value="billing_contact">Billing Contact</option>
                <option value="event_coordinator">Event Coordinator</option>
                <option value="general_staff">General Staff</option>
              </select>
            </div>
            <div>
              <label className="form-label">Email Frequency</label>
              <select className="form-select" value={form.contactFrequency} onChange={e => set('contactFrequency', e.target.value)}>
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="as_needed">As Needed</option>
                <option value="do_not_contact">Do Not Contact</option>
              </select>
            </div>
          </div>

          {attached && (
            <div>
              <label className="form-label">Portal Role</label>
              <select className="form-select" value={form.portalRole} onChange={e => set('portalRole', e.target.value)} style={{ maxWidth: 240 }}>
                <option value="member_contact">Member Contact (view-only)</option>
                <option value="member_admin">Member Admin (manage business)</option>
              </select>
              <small style={{ color: 'var(--gray-500)' }}>Max 2 Member Admins per business.</small>
            </div>
          )}

          {/* Politician / external org fields */}
          {!attached && (
            <fieldset style={{ border: '1px solid var(--gray-200)', borderRadius: 6, padding: 12 }}>
              <legend style={{ padding: '0 6px', fontSize: '0.85rem', color: 'var(--gray-500)' }}>For politicians or affiliated orgs</legend>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label className="form-label">Office / Organization</label>
                  <input className="form-input" placeholder="e.g. US Senate, Township Council" value={form.officeOrOrg} onChange={e => set('officeOrOrg', e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Government Level</label>
                  <select className="form-select" value={form.governmentLevel} onChange={e => set('governmentLevel', e.target.value)} style={{ maxWidth: 240 }}>
                    <option value="">— Not applicable —</option>
                    <option value="local">Local</option>
                    <option value="county">County</option>
                    <option value="state">State</option>
                    <option value="federal">Federal</option>
                  </select>
                </div>
              </div>
            </fieldset>
          )}
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-ghost"   onClick={onClose}    disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (isEdit ? 'Save' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
}
